<template>
  <div class="user-tab-security">
    <v-card class="mb-7">
      <v-card-title> {{ $t('employeeData') }} </v-card-title>

      <v-card-text>
        <v-form ref="dataForm" v-model="valid" @submit.prevent="handleFormSubmit">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="employeeDataLocal.firstName"
                outlined
                dense
                maxlength="100"
                :label="$t('firstNameRequired')"
                :placeholder="$t('firstName', { postfix: '' })"
                :rules="[validators.required]"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="employeeDataLocal.lastName"
                outlined
                dense
                maxlength="100"
                :label="$t('lastNameRequired')"
                :placeholder="$t('lastName', { postfix: '' })"
                :rules="[validators.required]"
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="employeeDataLocal.status"
                :label="$t('status', { postfix: '' })"
                :placeholder="$t('status', { postfix: '' })"
                :items="statusChoices"
                outlined
                dense
                clearable
                hide-details
              />
            </v-col>

            <v-col cols="12">
              <v-combobox
                v-model="employeeDataLocal.tags"
                :items="tagsChoices"
                :label="$t('tag')"
                :placeholder="$t('tag')"
                item-text="tag"
                item-value="id"
                multiple
                outlined
                dense
                hide-details
              >
                <template v-slot:selection="{ attrs, item, parent, selected }">
                  <v-chip
                    v-bind="attrs"
                    :input-value="selected"
                    small
                    label
                    color="info"
                    outlined
                    class="font-weight-semibold disable-min-height--chip mt-1"
                  >
                    <span class="pr-3">{{ item === Object(item) ? item.tag : item }}</span>
                    <v-icon x-small @click="parent.selectItem(item)"> $delete </v-icon>
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="employeeDataLocal.phone"
                outlined
                dense
                maxlength="31"
                :label="$t('phoneNumber')"
                :placeholder="$t('phoneNumber')"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="employeeDataLocal.languages"
                :label="$t('language')"
                :placeholder="$t('language')"
                :items="languageChoices"
                outlined
                dense
                multiple
                hide-details
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-menu
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="employeeDataLocal.workingStartDay"
                    :label="$t('startWorkingDay')"
                    :placeholder="$t('startWorkingDay')"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="employeeDataLocal.workingStartDay"
                  :first-day-of-week="1"
                  :locale="$i18n.locale"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="employeeDataLocal.maritalStatus"
                outlined
                dense
                maxlength="100"
                :label="$t('martialStatus')"
                :placeholder="$t('martialStatus')"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="employeeDataLocal.nationality"
                outlined
                dense
                maxlength="100"
                :label="$t('nationality')"
                :placeholder="$t('nationality')"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-switch v-model="employeeDataLocal.kids" :label="$t('kids')" class="mt-0" />
            </v-col>
          </v-row>

          <!-- Custom fields -->
          <v-row v-if="localCustomFields.length">
            <v-col cols="12">
              <v-divider class="mb-4" />
              <h3 class="text-lg font-weight-semibold mb-3">{{ $t('customFields') }}</h3>
            </v-col>
            <v-col
              v-for="(customField, index) in localCustomFields"
              :key="`CustomField-${customField.id}-${index}`"
              cols="12"
            >
              <custom-field-input :field="customField" />
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12">
              <v-btn
                color="primary"
                class="me-3"
                type="submit"
                :loading="loading"
                :disabled="!valid || !areDataChanged"
              >
                {{ $t('save') }}
              </v-btn>
              <v-btn outlined color="secondary" :disabled="!areDataChanged" @click="restoreDefaults">
                {{ $t('discard') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { useActions, useState } from 'vuex-composition-helpers'
import { required } from '@core/utils/validation'
import _ from 'lodash'
import { getCustomFieldsForEmployee } from '@/utils/customFieldUtils'
import employeeUtils from '@/utils/employeeUtils'
import CustomFieldInput from '@/views/_shared/CustomFieldInput.vue'

export default {
  components: {
    CustomFieldInput,
  },
  setup() {
    const { updateEmployee } = useActions('staff', ['updateEmployee'])
    const { employeeData } = useState('staff', ['employeeData'])

    const loading = ref(false)
    const valid = ref(true)
    const dataForm = ref(null)
    const localCustomFields = ref([])
    const employeeDataLocal = ref({})

    const {
      fetchTagChoices,
      fetchLanguageChoices,
      fetchStatusChoices,
      tagsChoices,
      languageChoices,
      statusChoices,
    } = employeeUtils()

    const syncLocalState = () => {
      employeeDataLocal.value = JSON.parse(JSON.stringify(employeeData.value))
      localCustomFields.value = _.cloneDeep(
        getCustomFieldsForEmployee(employeeData.value.customFields || []),
      )
    }

    syncLocalState()

    watch(
      () => employeeData.value,
      () => {
        syncLocalState()
      },
      { deep: true },
    )

    const handleFormSubmit = async () => {
      const isFormValid = dataForm.value.validate()
      if (!isFormValid) return

      loading.value = true
      await updateEmployee({
        id: employeeData.value.id,
        payload: {
          user: {
            firstName: employeeDataLocal.value.firstName,
            lastName: employeeDataLocal.value.lastName,
            phone: employeeDataLocal.value.phone,
          },
          workingStartDay: employeeDataLocal.value.workingStartDay,
          nationality: employeeDataLocal.value.nationality,
          maritalStatus: employeeDataLocal.value.maritalStatus,
          kids: employeeDataLocal.value.kids,
          status: employeeDataLocal.value.status,
          languages: employeeDataLocal.value.languages,
          tags: employeeDataLocal.value.tags.map((tag) => {
            if (_.isObject(tag)) return tag
            return { tag }
          }),
          customFieldsToWrite: localCustomFields.value,
        },
      })
      await fetchTagChoices()
      loading.value = false
    }

    const areDataChanged = computed(() => {
      const basicChanged =
        employeeDataLocal.value.firstName !== employeeData.value.firstName ||
        employeeDataLocal.value.lastName !== employeeData.value.lastName ||
        employeeDataLocal.value.workingStartDay !== employeeData.value.workingStartDay ||
        employeeDataLocal.value.phone !== employeeData.value.phone ||
        employeeDataLocal.value.nationality !== employeeData.value.nationality ||
        employeeDataLocal.value.maritalStatus !== employeeData.value.maritalStatus ||
        employeeDataLocal.value.kids !== employeeData.value.kids ||
        employeeDataLocal.value.status !== employeeData.value.status ||
        !_.isEmpty(_.differenceWith(employeeDataLocal.value.languages, employeeData.value.languages, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(employeeData.value.languages, employeeDataLocal.value.languages, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(employeeDataLocal.value.tags, employeeData.value.tags, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(employeeData.value.tags, employeeDataLocal.value.tags, _.isEqual))

      const employeeCustomFields = getCustomFieldsForEmployee(employeeData.value.customFields || [])
      const customFieldsChanged =
        !_.isEmpty(_.differenceWith(localCustomFields.value, employeeCustomFields, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(employeeCustomFields, localCustomFields.value, _.isEqual))

      return basicChanged || customFieldsChanged
    })

    const restoreDefaults = () => {
      syncLocalState()
    }

    onMounted(async () => {
      await Promise.all([fetchTagChoices(), fetchLanguageChoices(), fetchStatusChoices()])
    })

    return {
      handleFormSubmit,
      restoreDefaults,

      valid,
      loading,
      dataForm,
      employeeDataLocal,
      localCustomFields,
      areDataChanged,
      tagsChoices,
      statusChoices,
      languageChoices,

      validators: {
        required,
      },
    }
  },
}
</script>
